/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Table component for import personal data wizard
 *
 * @author Julia Ignatieva
 * @version 1.16.0
 * @since 1.16.0
 */

import { Fragment, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../components/common';
import { loadTableListAction } from '../../../../components/Table/actions/tableActions';
import { ITableField, ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableCount,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableIsLoading, makeSelectorTableRemovedRowIndexes,
} from '../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil, { ETableColumnUtilHideFilters } from '../../../../components/Table/TableColumnUtil';
import { ILoadTableListPayload, TableNameType } from '../../../../components/Table/types/tableTypes';
import { makeSelectTranslationCommon } from '../../../../modules/internationalization/intSelectors';
import { ICommonPageTranslation, ICommonTranslation } from '../../../../modules/internationalization/types';
import { ICommonPageTranslationTitle } from '../../../../modules/internationalization/types/pages/common';
import {
    IImportPersonalDataTableTranslation,
    IWizardImportPersonalDataTranslation,
} from '../../../../modules/internationalization/types/wizards/importPersonalData';
import { guardComponentType } from '../../../../modules/role/reducer/guardReducer';
import {
    makeSelectGuardComponents,
    makeSelectIsColumnResizeEnabled,
} from '../../../../modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { wizardImportPersonalDataInitValidatedList } from '../../wizardImportPersonalDataActions';
import { EImportPersonalDataField } from '../../wizardImportPersonalDataConstant';
import {
    getColumnCellType,
    getColumnFilterType,
    getColumnFixedType,
    importPersonalDataColumnNamesIfCamEnabled,
    importPersonalDataColumnsWidth,
} from './WizardImportPersonalDataTableCells';

interface IWizardImportPersonalDataTableProps {
    translation: IWizardImportPersonalDataTranslation;
}

export type TWizardImportPersonalDataTable = IWizardImportPersonalDataTableProps & IConnectedProps & IDispatchProps;

export const WizardImportPersonalDataTable = (props: TWizardImportPersonalDataTable) => {
    const tableColumnUtil = new TableColumnUtil<EImportPersonalDataField,
        ICommonPageTranslation<IImportPersonalDataTableTranslation> & ICommonTranslation>({
            cellConfig: importPersonalDataColumnsWidth,
            componentsFeature: props.componentsFeature,
            fields: props.fields as Array<ITableField<EImportPersonalDataField>>,
            isTableResizeEnabled: props.isTableResizeEnabled,
            getColumnCellType,
            getColumnFilterType,
            getColumnFixedType,
            columnNames: importPersonalDataColumnNamesIfCamEnabled,
            hideFiltersType: ETableColumnUtilHideFilters.EMPTY,
            isDisableSort: true,
            translation: {
                ...props.translationCommon,
                table: { ...props.translation.table },
                title: {} as ICommonPageTranslationTitle,
            },
        },
    );

    useEffect(() => {
        props.initValidatedList();
    }, []);

    return (
        <Fragment>
            <Table
                columns={tableColumnUtil.customizeTableColumns()}
                counter={props.tableCount}
                data={props.data}
                isDensity={props.isDensity}
                isLoading={props.isLoading}
                loadTableList={props.loadTableList}
                removedRowIndexes={props.removedRowIndexes}
                scrollbarBottom='64px'
                setTableSort={() => {
                }}
                sort={{}}
                tableDataName="importPersonalData"
            />
        </Fragment>
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    data: ITableReducer[TableNameType]['data'];
    fields: ITableReducer[TableNameType]['fields'];
    isDensity: boolean;
    isLoading: boolean;
    isTableResizeEnabled: boolean;
    removedRowIndexes: number[];
    tableCount: number;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.importPersonalData),
    fields: makeSelectorTableFields(TableNameType.importPersonalData),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(TableNameType.importPersonalData),
    isTableResizeEnabled: makeSelectIsColumnResizeEnabled(),
    removedRowIndexes: makeSelectorTableRemovedRowIndexes(TableNameType.importPersonalData),
    tableCount: makeSelectorTableCount(TableNameType.importPersonalData),
    translationCommon: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    initValidatedList: () => void;
    loadTableList: (payload?: ILoadTableListPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    initValidatedList: () => dispatch(wizardImportPersonalDataInitValidatedList()),
    loadTableList: (payload) => dispatch(loadTableListAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardImportPersonalDataTable);
